import { Configs } from 'Constants';
import { debounce } from 'throttle-debounce';
import React from 'react';

const DEBOUNCE_DELAY = 500;

export class SearchError extends Error {
  status: number;
  errorCode: string;

  constructor(status: number, message: string, errorCode: string) {
    super(message);
    this.status = status;
    this.errorCode = errorCode;
  }
}

export interface Search {
  displayedOutput: string;
  forced?: boolean;
}

export interface AddressSearch extends Search {
  streetNumber: string;
  additionalStreetNumber?: string;
  streetName: string;
  additionalAddress?: string;
  zipCode: string;
  city: string;
}

export interface ZipCodeSearch extends Search {
  name: string;
  insee: string;
}

export interface AddressSearchResponse {
  lines: AddressSearch[];
}

export interface AutoCompleteInput {
  input: string;
}

export interface AddressSearchInput extends AutoCompleteInput {
  city: string;
  postalCode: string;
  inseeCode: string;
}

const forcedSearch: Search = { displayedOutput: `Mon adresse n'est pas dans la liste, conserver ma saisie`, forced: true };

const isInstanceOfAddressSearch = (search?: Search): boolean =>
  undefined !== search && 'streetName' in search && 'zipCode' in search && 'city' in search;

const isInstanceOfZipCodeSearch = (search?: Search): search is ZipCodeSearch =>
  undefined !== search && 'name' in search && 'insee' in search;

const isInstanceOfAddressSearchInput = (input?: AutoCompleteInput): input is AddressSearchInput =>
  input !== undefined && 'city' in input && 'postalCode' in input && 'inseeCode' in input;

const fetchAddressesSearchWithDebounce = (
  setSearchResults: React.Dispatch<React.SetStateAction<Search[]>>,
  setSearchErrorResult?: React.Dispatch<React.SetStateAction<Search[]>>,
) =>
  debounce(
    DEBOUNCE_DELAY,
    (payload: AutoCompleteInput, resolve?: (searchResults: Search[]) => void, reject?: (error: SearchError) => void) => {
      if (!isInstanceOfAddressSearchInput(payload)) {
        return reject && reject(new SearchError(400, 'Invalid payload format for zip code search.', 'INVALID_PAYLOAD'));
      }
      const { input, city, postalCode, inseeCode } = payload;
      return fetchAddressesSearch(input, city, postalCode, inseeCode)
        .then((searchResults: Search[]) => {
          setSearchResults(searchResults);
          resolve && resolve(searchResults);
        })
        .catch((error) => {
          setSearchResults([]);
          setSearchErrorResult && setSearchErrorResult(error);
          reject && reject(error);
        });
    },
  );

const fetchZipCodeSearchWithDebounce = (
  setSearchResults: React.Dispatch<React.SetStateAction<Search[]>>,
  setSearchErrorResult?: React.Dispatch<React.SetStateAction<Search[]>>,
) =>
  debounce(
    DEBOUNCE_DELAY,
    ({ input }: AutoCompleteInput, resolve?: (searchResults: Search[]) => void, reject?: (error: SearchError) => void) =>
      fetchZipCodeSearch(input)
        .then((searchResults: Search[]) => {
          setSearchResults(searchResults);
          resolve && resolve(searchResults);
        })
        .catch((error) => {
          setSearchResults([]);
          setSearchErrorResult && setSearchErrorResult(error);
          reject && reject(error);
        }),
  );

const fetchZipCodeSearch = async (input: string) =>
  new Promise<ZipCodeSearch[]>((resolve, reject) => {
    fetch(Configs.zipCodeSearchUrl + '?' + new URLSearchParams({ zipcode: input }))
      .then((res) => {
        if (!res.ok) {
          return res.json().then((errorResponse) => {
            reject(
              new SearchError(
                res.status,
                errorResponse.message || 'An error occurred while fetching zip code search.',
                errorResponse.errorCode || 'UNKNOWN_ERROR',
              ),
            );
          });
        }
        return res.json();
      })
      .then((json) => {
        if (json.length === 0) {
          return reject(new SearchError(400, 'The zipcode input is invalid', 'INVALID_ZIP_CODE'));
        }
        resolve(json.map((zipCodeSearch: ZipCodeSearch) => ({ ...zipCodeSearch, displayedOutput: zipCodeSearch.name })));
      })
      .catch((error) => reject(error));
  });

const normalizeAddress = (address: string) =>
  address
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[’'´]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const fetchAddressesSearch = (input: string, city: string, postalCode: string, inseeCode: string) =>
  new Promise<Search[]>((resolve) => {
    fetch(
      Configs.addressesSearchUrl +
        '?' +
        new URLSearchParams({
          input: normalizeAddress(input),
          postalCode,
          localityId: inseeCode,
          city,
        }),
    )
      .then((res) => {
        if (!res.ok) {
          resolve([forcedSearch]);
        }
        return res.json();
      })
      .then((json) => json as AddressSearchResponse)
      .then((response) => resolve([...response.lines.slice(0, 5), forcedSearch]))
      .catch(() => resolve([]));
  });

const fetchAddressValidation = (streetName: string, postalCode: string, city: string): Promise<boolean> =>
  fetch(Configs.addressValidationUrl + '?' + new URLSearchParams({ streetName, postalCode, city }))
    .then((response) => response.json())
    .then((json) => json as boolean)
    .catch(() => false);

export const AddressSearchApi = {
  fetchAddressesSearch,
  fetchAddressesSearchWithDebounce,
  fetchZipCodeSearch,
  fetchZipCodeSearchWithDebounce,
  isInstanceOfAddressSearch,
  isInstanceOfZipCodeSearch,
  fetchAddressValidation,
};
